import React, { useState, useContext, useMemo, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import { BannerContext } from '../../contexts/BannerContext';
import { useJournalModal } from '../../contexts/JournalModalContext';
import { TradingContext } from '../../contexts/TradingContext';
import TradeJournalList from '../TradeJournal/TradeJournalsList';
import JournalTemplateSelector from '../TradeJournal/JournalTemplateSelector';
import DashboardMobileTabHeader from './DashboardMobileTabHeader';
import PrimaryButton from '../shared/PrimaryButton';
import JournalCalendar from '../TradingRules/JournalCalendar';
import JournalDayModal from '../TradingRules/JournalDayModal';
import { MOBILE_BOTTOM_NAV_CLEARANCE } from '../../constants/mobileBottomNav';
import { useJournalCalendar } from '../../hooks/useJournalCalendar';
import { useTradeJournals } from '../../hooks/useTradeJournals';
import { normalizeJournalTemplate } from '../../hooks/useJournalTemplateBuilder';
import useTradingRulesMetrics from '../../hooks/useTradingRulesMetrics';
import { useRealtimeAccountMetrics } from '../../hooks/useRealtimeAccountMetrics';

const pnlFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Journal page for mobile navigation
 * Contains: header, journal stats, calendar, add journal button, and journal list
 */
const JournalPage = ({ menuCurrentPage = 'journal', hideHeader = false }) => {
  const { colors } = useTheme();
  const { showMessage } = useContext(BannerContext);
  const { openNewJournal } = useJournalModal();
  const { saveTradeJournalTemplate } = useTradeJournals();
  const { selectedAccount } = useContext(TradingContext);
  const accountId = selectedAccount?.AccountID;
  const stableAccount = useMemo(
    () => (accountId ? { AccountID: accountId } : null),
    [accountId]
  );

  const handleSelectTemplate = useCallback(async (template, meta = {}) => {
    if (meta?.saved) return;
    try {
      const normalized = normalizeJournalTemplate(template);
      await saveTradeJournalTemplate(normalized, template.name);
      showMessage(`Template "${template.name}" loaded.`, 'success');
    } catch {
      showMessage('Error loading template', 'danger');
    }
  }, [saveTradeJournalTemplate, showMessage]);

  const [journalMonth, setJournalMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [journalDayModal, setJournalDayModal] = useState(null);

  const { days: journalCalendarDays } = useJournalCalendar({
    month: journalMonth,
    accountId: accountId || undefined,
    paperTrading: selectedAccount?.isPaperTrading,
  });

  const {
    journalCompliance: weekCompliance,
    loading: weekLoading,
  } = useTradingRulesMetrics(stableAccount, '7days');

  const {
    journalCompliance: monthCompliance,
    loading: monthLoading,
  } = useTradingRulesMetrics(stableAccount, '30days');

  const realtimeMetrics = useRealtimeAccountMetrics();
  const todayPnl = Number(realtimeMetrics?.todayChange);
  const todayPnlReady = realtimeMetrics?.isReady && Number.isFinite(todayPnl);

  const weekTrades = weekCompliance?.closed_positions ?? weekCompliance?.new_positions_opened ?? 0;
  const weekJournaled = weekCompliance?.journals_created ?? 0;
  const monthPct = monthCompliance?.compliance_percentage ?? (
    (monthCompliance?.closed_positions ?? monthCompliance?.new_positions_opened ?? 0) > 0
      ? Math.min(
          100,
          ((monthCompliance?.journals_created ?? 0) /
            (monthCompliance?.closed_positions ?? monthCompliance?.new_positions_opened ?? 1)) *
            100
        )
      : 0
  );

  const statsLoading = weekLoading || monthLoading;

  const sectionTitleStyle = {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: colors.text,
    marginBottom: 12,
  };

  const statLabelStyle = {
    fontSize: '0.63rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: 4,
  };

  const statValueStyle = {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    color: colors.text,
    fontFamily: 'monospace',
    lineHeight: 1.2,
  };

  const pnlColor = !todayPnlReady
    ? colors.textMuted
    : todayPnl > 0
      ? colors.success
      : todayPnl < 0
        ? colors.danger
        : colors.textMuted;

  return (
    <div
      style={{
        height: hideHeader ? '100%' : '100dvh',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: hideHeader ? 0 : 'env(safe-area-inset-top)',
        backgroundColor: colors.bgPrimary,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: hideHeader ? 0 : MOBILE_BOTTOM_NAV_CLEARANCE,
      }}
    >
      {!hideHeader && (
        <DashboardMobileTabHeader title="Journals" menuCurrentPage={menuCurrentPage} />
      )}

      <div style={{ padding: '16px 12px 0', display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0 }}>
        <div
          style={{
            backgroundColor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            padding: '14px 16px',
          }}
        >
          <div style={sectionTitleStyle}>Journal Activity</div>

          {statsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                gap: 0,
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                <div style={statLabelStyle}>P&L Today</div>
                <div style={{ ...statValueStyle, color: pnlColor, fontSize: '1.05rem' }}>
                  {todayPnlReady ? pnlFormatter.format(todayPnl) : '—'}
                </div>
              </div>

              <div
                style={{
                  width: 1,
                  alignSelf: 'stretch',
                  backgroundColor: colors.border,
                  flexShrink: 0,
                }}
              />

              <div style={{ flex: 1, minWidth: 0, padding: '0 10px' }}>
                <div style={statLabelStyle}>Last 7 days</div>
                <div style={statValueStyle}>
                  {weekJournaled} / {weekTrades}
                </div>
              </div>

              <div
                style={{
                  width: 1,
                  alignSelf: 'stretch',
                  backgroundColor: colors.border,
                  flexShrink: 0,
                }}
              />

              <div style={{ flex: 1, minWidth: 0, paddingLeft: 10 }}>
                <div style={statLabelStyle}>Last 30 days</div>
                <div style={statValueStyle}>{Math.round(monthPct)}%</div>
              </div>
            </div>
          )}
        </div>

        <JournalCalendar
          days={journalCalendarDays}
          month={journalMonth}
          onMonthChange={setJournalMonth}
          onDayClick={(dateStr) => setJournalDayModal(dateStr)}
          legendBelow
          fillHeight={false}
        />
      </div>

      <div
        style={{
          padding: '16px 12px 12px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <PrimaryButton
          onClick={() => openNewJournal()}
          size="sm"
          variant="compact"
          style={{
            fontSize: '14px',
            minHeight: '40px',
            minWidth: 'auto',
            paddingLeft: '16px',
            paddingRight: '16px',
          }}
        >
          + Journal
        </PrimaryButton>
        <JournalTemplateSelector
          showCard={false}
          buttonLabel="Templates"
          editBeforeSave
          onSelectTemplate={handleSelectTemplate}
        />
      </div>

      <div style={{ padding: '0 12px', flex: '0 0 auto', position: 'relative', zIndex: 0 }}>
        <div id="journals-container" style={{ position: 'relative' }}>
          <TradeJournalList />
        </div>
      </div>

      <JournalDayModal
        show={!!journalDayModal}
        onHide={() => setJournalDayModal(null)}
        dateStr={journalDayModal}
        accountId={accountId}
        paperTrading={selectedAccount?.isPaperTrading}
      />
    </div>
  );
};

export default JournalPage;
